import React, { useEffect } from "react";
import { atom, useAtom } from "jotai";
import useStore from "../../../../context/useStore";
import useDossierStats from "./useDossierStats";
import KpiBand from "./KpiBand";
import StatusProgress from "./StatusProgress";
import QualityChecks from "./QualityChecks";
import RentComparator from "./RentComparator";
import MarginAnalysis from "./MarginAnalysis";
import DurKmMatrix from "./DurKmMatrix";
import VehicleMix from "./VehicleMix";
import BenefitsCoverage from "./BenefitsCoverage";
import styles from "./DossierDashboard.module.scss";

const dashboardOpenAtom = atom(false);

// Même liste de rôles que useRoleStyle (masquage des champs de marge)
const MARGIN_HIDDEN_ROLES = ["COMMERCIAL_JUNIOR", "COMMERCIAL_SENIOR"];

const DossierDashboard = ({
  quotations,
  constantData,
  filters,
  setFilters,
  onScrollToQuotation,
}) => {
  const [state] = useStore();
  const [open, setOpen] = useAtom(dashboardOpenAtom);
  // Volet fermé : tableau vide pour ne pas calculer (ni déclencher le toast
  // TVA de getTVAByCatalog) tant que rien n'est affiché
  const stats = useDossierStats(open ? quotations : [], constantData);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, setOpen]);

  if (!quotations || quotations.length === 0) return null;

  const hideMargins = MARGIN_HIDDEN_ROLES.includes(state.auth.user?.role);

  const toggleStatus = (id) =>
    setFilters({
      ...filters,
      status: filters.status === String(id) ? "" : String(id),
    });

  const toggleBrand = (name) => {
    const v = name.toLowerCase();
    setFilters({ ...filters, brand: filters.brand === v ? "" : v });
  };

  const toggleDurKm = (dur, km) => {
    const isActive =
      filters.duration === String(dur) && filters.km === String(km);
    setFilters({
      ...filters,
      duration: isActive ? "" : String(dur),
      km: isActive ? "" : String(km),
    });
  };

  // On ferme le volet pour laisser voir la ligne ciblée
  const scrollTo = (index) => {
    setOpen(false);
    onScrollToQuotation(index);
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          className={`${styles.tab} no-print`}
          onClick={() => setOpen(true)}
          title="Ouvrir l'analyse du dossier"
        >
          <i className="fa fa-chart-bar" />
          Analyse
        </button>
      )}
      {open && (
        <>
          <div className={styles.overlay} onClick={() => setOpen(false)} />
          <aside
            className={styles.drawer}
            role="dialog"
            aria-label="Analyse du dossier"
          >
            <div className={styles.head}>
              <div>
                <h4>Analyse du dossier</h4>
                <div className={styles.headSub}>
                  {stats.count} devis · mise à jour en direct pendant l'édition
                </div>
              </div>
              <button
                type="button"
                className={styles.closeBtn}
                onClick={() => setOpen(false)}
                title="Fermer"
              >
                <i className="fa fa-times" />
              </button>
            </div>
            <div className={styles.body}>
              <KpiBand stats={stats} />
              <StatusProgress
                stats={stats}
                activeStatus={filters.status}
                onToggleStatus={toggleStatus}
              />
              <QualityChecks stats={stats} onScrollTo={scrollTo} />
              <RentComparator stats={stats} onScrollTo={scrollTo} />
              {!hideMargins && (
                <MarginAnalysis stats={stats} onScrollTo={scrollTo} />
              )}
              <DurKmMatrix
                stats={stats}
                filters={filters}
                onToggleDurKm={toggleDurKm}
              />
              <VehicleMix
                stats={stats}
                filters={filters}
                onToggleBrand={toggleBrand}
              />
              <BenefitsCoverage stats={stats} />
            </div>
          </aside>
        </>
      )}
    </>
  );
};

export default DossierDashboard;
